import type { Schemas } from '@kanos/api-client';
import { Skeleton, useAccent } from '@kanos/ui';
import { api, failure, initials } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

type PortalBranding = Schemas['PortalBranding'];

/** No firm known (yet): the Kanos portal itself, in the Kanos colours. */
const KANOS: PortalBranding = {
  firmName: null,
  accentColor: null,
  imprintUrl: null,
  privacyUrl: null,
  phone: null,
  email: null,
  closed: false,
};

/**
 * Whose portal this is (ADR-020): the API knows the firm by the host the portal was called
 * under. Branding is decoration - if the question fails, the portal stays usable in the Kanos
 * colours and says nothing in the firm's name.
 */
export function useBranding(): PortalBranding | undefined {
  const branding = useQuery({
    queryKey: ['portal', 'branding'],
    queryFn: async (): Promise<PortalBranding> => {
      const { data, error, response } = await api.GET('/api/v1/portal/branding');
      if (!data) throw failure('getPortalBranding', error, response.status);
      return data;
    },
    staleTime: Infinity,
  });
  const resolved = branding.isError ? KANOS : branding.data;
  useAccent(resolved?.accentColor ?? null);
  return resolved;
}

/** The firm in the header, or the Kanos wordmark; empty while the answer is on its way. */
export function FirmBadge({ branding }: { branding: PortalBranding | undefined }) {
  if (branding === undefined) {
    return <Skeleton rounded="full" className="h-11 w-40" />;
  }
  if (branding.firmName === null) {
    return <p className="text-xl font-bold tracking-[-0.03em]">kanos</p>;
  }
  return (
    <div className="glass flex min-w-0 items-center gap-2.5 rounded-full py-1.5 pr-4 pl-1.5">
      <FirmMark name={branding.firmName} />
      <p className="line-clamp-2 text-sm leading-tight font-semibold">{branding.firmName}</p>
    </div>
  );
}

/**
 * The firm's logo (ADR-041), its initials while it loads or where it has none - the name
 * beside it says who it is, so the image itself is decoration.
 */
function FirmMark({ name }: { name: string }) {
  const [logo, setLogo] = useState<'loading' | 'shown' | 'none'>('loading');
  return (
    <>
      {logo !== 'none' && (
        <img
          src="/api/v1/portal/branding/logo"
          alt=""
          onLoad={() => setLogo('shown')}
          onError={() => setLogo('none')}
          className={logo === 'shown' ? 'h-8 w-auto max-w-32 shrink-0 object-contain' : 'hidden'}
        />
      )}
      {logo !== 'shown' && (
        <span
          aria-hidden
          className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-text text-xs font-bold text-ink-contrast"
        >
          {initials(name, null)}
        </span>
      )}
    </>
  );
}
